<template>
  <div class="designer forms" dir="rtl">
    <header class="fl-hero">
      <div>
        <span class="vn-kicker"><b>BUILDER</b> {{ isVisit ? 'ساختار ویزیت‌ها' : 'ساختار پرسشنامه‌ها' }}</span>
        <h1>{{ isVisit ? 'نوع‌های ویزیت را طراحی کنید' : 'پرسشنامه‌ها را طراحی کنید' }}</h1>
        <p>{{ isVisit
          ? 'بخش‌ها، پرسش‌ها، قانون‌های پاسخ و مراحل تصویر هر نوع ویزیت را با کشیدن و رها کردن بچینید؛ اپ میدانی پس از ذخیره همان را نشان می‌دهد.'
          : 'پرسش‌ها، گزینه‌ها، قانون‌های پاسخ و تصاویر هر پرسشنامه را با کشیدن و رها کردن بچینید.' }}</p>
      </div>
      <div class="fl-hero-actions">
        <router-link to="/designer/rules" class="fl-ghost"><VnIcon name="shield" />قوانین اعتبارسنجی</router-link>
        <button type="button" class="fl-primary" @click="openWizard()"><VnIcon name="plus" />{{ isVisit ? 'نوع ویزیت تازه' : 'پرسشنامه‌ی تازه' }}</button>
      </div>
      <span class="vn-bars fl-bars" aria-hidden="true" />
    </header>

    <div class="fl-tools">
      <label class="fl-search"><VnIcon name="search" /><input v-model="query" type="search" :placeholder="isVisit ? 'جست‌وجوی نوع ویزیت' : 'جست‌وجوی پرسشنامه'" /></label>
      <div class="ins-seg" role="group" aria-label="وضعیت">
        <button v-for="item in filters" :key="item.key" type="button" :class="{ active: filter === item.key }" @click="filter = item.key">{{ item.label }} <span class="vn-num">{{ fa(item.count) }}</span></button>
      </div>
    </div>

    <div v-if="error" class="fl-error" role="alert">{{ error }} <button type="button" class="ins-link" @click="load">تلاش دوباره</button></div>
    <div v-if="loading && !rows.length" class="fl-grid"><span v-for="n in 6" :key="n" class="fl-skeleton" /></div>
    <EmptyState v-else-if="!loading && !shown.length && !error" kind="search" :title="rows.length ? 'موردی با این جست‌وجو پیدا نشد' : 'هنوز فرمی نساخته‌اید'"
                :description="rows.length ? '' : 'با «تازه» نخستین فرم را بسازید.'" />

    <div class="fl-grid">
      <article v-for="row in shown" :key="row.id" class="fl-card" :class="{ inactive: !row.is_active }">
        <header>
          <span class="fl-icon"><VnIcon :name="isVisit ? 'audit' : 'survey'" /></span>
          <div class="fl-title"><h2>{{ row.verbose_name }}</h2><small v-if="row.title" dir="ltr">{{ row.title }}</small></div>
          <span class="fl-state" :class="row.is_active ? 'on' : 'off'">{{ row.is_active ? 'فعال' : 'غیرفعال' }}</span>
        </header>
        <p v-if="row.description" class="fl-desc">{{ row.description }}</p>
        <dl class="fl-stats">
          <div><dt>بخش</dt><dd class="vn-num">{{ fa(row.sections) }}</dd></div>
          <div><dt>پرسش</dt><dd class="vn-num">{{ fa(row.questions) }}</dd></div>
          <div><dt>تصویر</dt><dd class="vn-num">{{ fa(row.photos) }}</dd></div>
          <div><dt>{{ isVisit ? 'ویزیت' : 'پاسخ‌نامه' }}</dt><dd class="vn-num">{{ fa(isVisit ? row.visits : row.fills) }}</dd></div>
        </dl>
        <div class="fl-tags">
          <span v-if="row.has_supervision" class="tag"><VnIcon name="shield" />سرکشی</span>
          <span v-if="row.has_phone_verification" class="tag"><VnIcon name="phone" />تأیید شماره</span>
        </div>
        <footer>
          <router-link :to="`${base}/${row.id}`" class="fl-open">باز کردن در طراح<VnIcon name="arrow" /></router-link>
          <button type="button" class="ins-link" @click="openWizard(row.id)"><VnIcon name="duplicate" />رونوشت</button>
        </footer>
      </article>
    </div>

    <NewFormWizard v-if="wizard" :kind="kind" :sources="rows" :categories="categories" :preset="wizard" @close="wizard = null" @created="created" />
  </div>
</template>

<script>
import VnIcon from '@/components/VnIcon/index.vue';
import EmptyState from '@/components/EmptyState/index.vue';
import NewFormWizard from './NewFormWizard.vue';
import { fa } from './designerModel';
import './designer.css';

export default {
  name: 'FormList',
  components: { VnIcon, EmptyState, NewFormWizard },
  data: () => ({ rows: [], categories: [], loading: true, error: '', query: '', filter: 'active', wizard: null }),
  computed: {
    kind() { return this.$route.meta.kind; },
    isVisit() { return this.kind === 'visit'; },
    base() { return this.isVisit ? '/designer/visits' : '/designer/surveys'; },
    project() { return this.$STORE.state.userConfig.setProjectId; },
    filters() {
      const active = this.rows.filter(row => row.is_active).length;
      return [{ key: 'active', label: 'فعال', count: active }, { key: 'inactive', label: 'غیرفعال', count: this.rows.length - active }, { key: 'all', label: 'همه', count: this.rows.length }];
    },
    shown() {
      const q = this.query.trim().toLowerCase();
      return this.rows.filter(row => (this.filter === 'all' || (this.filter === 'active') === row.is_active)
        && (!q || `${row.verbose_name} ${row.title}`.toLowerCase().includes(q)));
    },
  },
  watch: { '$route.meta.kind'() { this.load(); }, project() { this.load(); } },
  mounted() { this.load(); },
  methods: {
    fa,
    async load() {
      this.loading = true; this.error = '';
      const [list, catalog] = await Promise.all([
        this.$ApiServiceLayer.get(`/core/api/admin/designer/${this.isVisit ? 'visit_types' : 'surveys'}/?p=${this.project}`),
        this.isVisit ? this.$ApiServiceLayer.get(`/core/api/admin/designer/catalog/?p=${this.project}`) : Promise.resolve(null),
      ]);
      this.loading = false;
      if (list.status !== 200) { this.error = this.$ApiServiceLayer.getErrorMessage(list); this.rows = []; return; }
      this.rows = list.data;
      if (catalog && catalog.status === 200) this.categories = catalog.data.outlet_categories;
      if (!this.rows.some(row => row.is_active) && this.rows.length) this.filter = 'all';
    },
    openWizard(copyFrom = null) { this.wizard = { copyFrom }; },
    created(id) { this.wizard = null; this.$router.push(`${this.base}/${id}`); },
  },
};
</script>

<style scoped>
.forms { display: grid; gap: 16px; }
.fl-hero { position: relative; display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 16px; padding: 22px 22px 25px; border-radius: var(--r-md); background: var(--navy-900); color: #fff; overflow: hidden; }
.fl-hero .vn-kicker { color: var(--shell-text-2); }
.fl-hero h1 { margin: 6px 0 4px; font-size: 20px; font-weight: 900; color: #fff; }
.fl-hero p { margin: 0; max-width: 620px; font-size: 12.5px; line-height: 1.9; color: var(--shell-text-2); }
.fl-bars { position: absolute; inset-inline: 0; bottom: 0; direction: ltr; }
.fl-hero-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.fl-primary, .fl-ghost { display: inline-flex; align-items: center; gap: 8px; min-height: 42px; padding: 0 16px; border-radius: var(--r-xs); font: inherit; font-size: 13px; font-weight: 900; cursor: pointer; text-decoration: none; }
.fl-primary { border: 2px solid var(--teal-500); background: var(--teal-500); color: var(--navy-900); box-shadow: 3px 3px 0 var(--pink-500); --icon-accent: var(--navy-900); }
.fl-ghost { border: 1px solid var(--shell-line); background: var(--navy-850); color: #fff; }
.fl-ghost:hover { background: var(--navy-800); color: #fff; text-decoration: none; }
.fl-tools { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px; }
#app .fl-search { display: flex; align-items: center; gap: 8px; flex: 1 1 260px; max-width: 420px; min-height: 40px; margin: 0; padding: 0 12px; border: 1px solid var(--line-strong); border-radius: var(--r-xs); background: var(--surface); color: var(--text-3); }
#app .fl-search input { flex: 1; min-width: 0; height: 38px; border: 0; background: transparent; box-shadow: none; padding: 0; font: inherit; font-size: 13px; }
#app .fl-search input:focus { outline: none; }
.fl-tools .ins-seg { background: var(--surface); border: 1px solid var(--line); }
.fl-error { padding: 10px 14px; border-radius: var(--r-sm); background: var(--pink-50); color: var(--pink-700); font-size: 13px; font-weight: 700; }
.fl-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(290px, 1fr)); gap: 14px; }
.fl-skeleton { height: 210px; border-radius: var(--r-md); background: linear-gradient(90deg, var(--navy-50), var(--surface), var(--navy-50)); background-size: 200% 100%; animation: shimmer 1.4s linear infinite; }
@keyframes shimmer { to { background-position: -200% 0; } }
.fl-card { display: grid; gap: 12px; align-content: start; padding: 16px; border: 1px solid var(--line); border-radius: var(--r-md); background: var(--surface); transition: border-color .15s, box-shadow .15s, transform .15s; }
.fl-card:hover { border-color: var(--navy-900); box-shadow: var(--shadow-hard); transform: translate(-2px, -2px); }
.fl-card.inactive { background: var(--surface-sunken); }
.fl-card header { display: flex; align-items: flex-start; gap: 10px; }
.fl-icon { flex: none; width: 38px; height: 38px; display: grid; place-items: center; border-radius: var(--r-xs); background: var(--navy-900); color: #fff; --icon-accent: var(--amber-500); }
.fl-title { flex: 1; min-width: 0; display: grid; }
.fl-title h2 { margin: 0; font-size: 14.5px; font-weight: 900; color: var(--text); line-height: 1.7; }
.fl-title small { font-size: 11px; color: var(--text-3); text-align: right; }
.fl-state { padding: 2px 8px; border-radius: var(--r-xs); font-size: 11px; font-weight: 800; }
.fl-state.on { background: var(--teal-50); color: var(--teal-700); }
.fl-state.off { background: var(--navy-100); color: var(--text-3); }
.fl-desc { margin: 0; font-size: 12px; color: var(--text-3); line-height: 1.8; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.fl-stats { display: grid; grid-template-columns: repeat(4, 1fr); margin: 0; border: 1px solid var(--line); border-radius: var(--r-sm); overflow: hidden; }
.fl-stats > div { display: grid; gap: 2px; padding: 8px; text-align: center; }
.fl-stats > div + div { border-inline-start: 1px solid var(--line); }
.fl-stats dt { font-size: 10.5px; font-weight: 700; color: var(--text-3); }
.fl-stats dd { margin: 0; font-size: 17px; font-weight: 900; color: var(--navy-900); }
.fl-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.fl-tags:empty { display: none; }
.tag { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: var(--r-xs); background: var(--navy-50); font-size: 11px; font-weight: 700; color: var(--text-2); }
.tag .vn-icon { width: 13px; height: 13px; }
.fl-card footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding-top: 10px; border-top: 1px dashed var(--line); }
.fl-open { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 900; color: var(--navy-900); text-decoration: none; }
.fl-open .vn-icon { width: 16px; height: 16px; }
.fl-open:hover { color: var(--teal-700); text-decoration: none; }
</style>
